import {
  AccountTree as PortfolioIcon,
  Dashboard as DashboardIcon,
  Inbox as FeedIcon,
  Tune as ConfigIcon,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import AccountPortfolio from "./AccountPortfolio";
import AccountWorkspace from "./AccountWorkspace";
import Configuration from "./Configuration";
import Dashboard from "./Dashboard";
import IntelligenceFeed from "./IntelligenceFeed";
import StreamDetail from "./StreamDetail";

// ─── Types ─────────────────────────────────────────────────────────────────────

type ViewKind = "dashboard" | "accounts" | "feed" | "config" | "account" | "stream";

interface NavItem {
  view: ViewKind;
  icon: React.ReactNode;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { view: "dashboard", icon: <DashboardIcon />, label: "Dashboard" },
  { view: "accounts", icon: <PortfolioIcon />, label: "Account Portfolio" },
  { view: "feed", icon: <FeedIcon />, label: "Feed" },
  { view: "config", icon: <ConfigIcon />, label: "Configuration" },
];

// ─── Sub-sidebar (full, with labels) ─────────────────────────────────────────

const SubSidebar: React.FC<{
  activeView: ViewKind;
  onNavigate: (view: ViewKind) => void;
}> = ({ activeView, onNavigate }) => (
  <Box
    sx={{
      width: 200,
      height: "100%",
      backgroundColor: "background.paper",
      borderRight: 1,
      borderColor: "divider",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
    }}
  >
    <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: "divider" }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        ACCOUNT INTELLIGENCE
      </Typography>
    </Box>
    <Box sx={{ flex: 1, py: 1 }}>
      {NAV_ITEMS.map((item) => {
        const isActive = activeView === item.view;
        return (
          <Box
            key={item.view}
            onClick={() => onNavigate(item.view)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1,
              cursor: "pointer",
              color: isActive ? "primary.main" : "text.secondary",
              backgroundColor: isActive ? "action.selected" : "transparent",
              "&:hover": { backgroundColor: "action.hover" },
              transition: "background-color 0.15s",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", "& svg": { fontSize: 18 } }}>
              {item.icon}
            </Box>
            <Typography variant="body2" fontWeight={isActive ? 700 : 400}>
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);

// ─── Collapsed sub-sidebar (icon-only for detail views) ───────────────────────

const CollapsedSubSidebar: React.FC<{
  activeView: ViewKind;
  onNavigate: (view: ViewKind) => void;
}> = ({ activeView, onNavigate }) => (
  <Box
    sx={{
      width: 48,
      height: "100%",
      backgroundColor: "background.paper",
      borderRight: 1,
      borderColor: "divider",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      py: 1,
      flexShrink: 0,
    }}
  >
    {NAV_ITEMS.map((item) => {
      const isActive =
        activeView === item.view ||
        (item.view === "accounts" && activeView === "account") ||
        (item.view === "dashboard" && activeView === "stream");
      return (
        <Tooltip key={item.view} title={item.label} placement="right">
          <IconButton
            size="small"
            onClick={() => onNavigate(item.view)}
            sx={{
              mb: 0.5,
              color: isActive ? "primary.main" : "text.secondary",
              backgroundColor: isActive ? "action.selected" : "transparent",
              borderRadius: 1,
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            {item.icon}
          </IconButton>
        </Tooltip>
      );
    })}
  </Box>
);

// ─── Main orchestrator ─────────────────────────────────────────────────────────

const AccountIntelligenceView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawView = searchParams.get("view") ?? "dashboard";
  const activeView: ViewKind = [
    "dashboard",
    "accounts",
    "feed",
    "config",
    "account",
    "stream",
  ].includes(rawView)
    ? (rawView as ViewKind)
    : "dashboard";

  const streamId = searchParams.get("id") ?? "";
  const accountId = searchParams.get("accountId") ?? "";
  const accountStreamId = searchParams.get("streamId") ?? "";

  const navigate = useCallback(
    (view: ViewKind, params?: Record<string, string>) => {
      const next = new URLSearchParams({ view, ...params });
      setSearchParams(next);
    },
    [setSearchParams],
  );

  const handleOpenStream = (id: string): void => navigate("stream", { id });
  const handleOpenAccount = (aId: string, sId: string): void =>
    navigate("account", { accountId: aId, streamId: sId });

  // Dashboard → Portfolio pre-filtered to a specific ICP (via streamId param)
  const handleOpenPortfolio = (sid?: string): void => {
    if (sid) navigate("accounts", { filterStream: sid });
    else navigate("accounts");
  };

  const handleOpenFeed = (): void => navigate("feed");

  const isDetailView = activeView === "stream" || activeView === "account";

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Sub-sidebar */}
      {isDetailView ? (
        <CollapsedSubSidebar activeView={activeView} onNavigate={(v) => navigate(v)} />
      ) : (
        <SubSidebar activeView={activeView} onNavigate={(v) => navigate(v)} />
      )}

      {/* Main content */}
      <Box sx={{ flex: 1, minWidth: 0, height: "100%", overflow: "hidden" }}>
        {activeView === "dashboard" && (
          <Dashboard
            onOpenFeed={handleOpenFeed}
            onOpenPortfolio={handleOpenPortfolio}
            onOpenStream={handleOpenStream}
          />
        )}
        {activeView === "accounts" && <AccountPortfolio />}
        {activeView === "feed" && <IntelligenceFeed />}
        {activeView === "config" && <Configuration />}
        {activeView === "stream" && streamId && (
          <StreamDetail
            streamId={streamId}
            onBack={() => navigate("dashboard")}
            onOpenAccount={handleOpenAccount}
          />
        )}
        {activeView === "stream" && !streamId && (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">No stream selected.</Typography>
          </Box>
        )}
        {activeView === "account" && accountId && accountStreamId && (
          <AccountWorkspace
            accountId={accountId}
            streamId={accountStreamId}
            onBack={() => navigate("accounts")}
          />
        )}
        {activeView === "account" && (!accountId || !accountStreamId) && (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">No account selected.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AccountIntelligenceView;
